import "./Table.scss";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import { Typography } from "core/typography/Typography";
import { useEffect, useState } from "react";
import { displayField, formatData } from "utils/balancesheetHelper";
import Icon from "core/icon/Icon";
import {
  BALANCESHEET_TABLE_ICON_SETTINGS,
  FIELDS_TYPES,
} from "constants/constants";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";
import PropTypes from "prop-types";

const isFilledTableCellValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const rowContainsUserData = (row) =>
  Object.entries(row || {}).some(
    ([key, value]) => key !== "id" && isFilledTableCellValue(value)
  );

const getDefaultTableFieldValue = (field) => {
  const parsedOptions = field?.options ? JSON.parse(field.options) : {};
  const firstOptionValue = parsedOptions?.values?.[0]?.val;

  switch (field?.type) {
    case FIELDS_TYPES.INLINE_RADIO:
    case FIELDS_TYPES.VERTICAL_RADIO:
    case FIELDS_TYPES.RANGE:
      return field?.default ?? firstOptionValue ?? "";
    case FIELDS_TYPES.INLINE_CHECK:
    case FIELDS_TYPES.VERTICAL_CHECK:
      return field?.default ?? "[]";
    case FIELDS_TYPES.CHOICE:
    case FIELDS_TYPES.DATE:
    case FIELDS_TYPES.NUMBER:
      return field?.default ?? "";
    default:
      return "";
  }
};

const mapTableRowsToFormFields = (rows, fields) =>
  _.chain(rows)
    .map((row, index) => {
      const rowContainsData = rowContainsUserData(row);

      return Object.keys(row).reduce((prev, curr) => {
        if (curr === "id") return prev;

        const field = fields.find((item) => String(item?.field_id) === curr);
        const rawValue = row[curr];
        const value =
          isFilledTableCellValue(rawValue) || !rowContainsData
            ? rawValue
            : getDefaultTableFieldValue(field);

        return [
          ...prev,
          {
            id: `${curr}_${index + 1}`,
            field_id: Number(curr),
            value,
          },
        ];
      }, []);
    })
    .flattenDeep()
    .value();

const TableBalancesheet = ({
  tableHeader,
  isEditable,
  fields,
  firstRowValues,
  columnFieldIds,
  onChange,
  formattedStructure,
  readOnly,
  values,
  rows,
  setRows,
  moduleId,
  isValidated,
  errors,
  isExport
}) => {
  const showEmptyRowOnInit = formattedStructure?.showEmptyRowOnInit;
  const [showErrorPopup, setShowErrorPopup] = useState(null);

  useEffect(() => {
    // 1) if rows already exist (pre-filled or user-added), keep them and do not reset
    if (rows?.length > 0) return;

    // 2) if we have fields from the backend, build rows from them
    if (fields?.length > 0) {
      const formatted = formatData(fields);
      const rowsWithUserData = formatted.filter(rowContainsUserData);

      if (!isEditable) {
        setRows(formatted);
      } else if (rowsWithUserData.length > 0) {
        setRows(rowsWithUserData);
      } else if (showEmptyRowOnInit) {
        handleAddRow();
      } else {
        setRows([]);
      }
      return;
    }

    // 3) if we have nothing yet, optionally add one empty row
    if (showEmptyRowOnInit) {
      handleAddRow();
    }
  }, [fields, isEditable, moduleId, showEmptyRowOnInit]);
  const syncRowsToForm = (updatedRows) => {
    onChange(mapTableRowsToFormFields(updatedRows, fields));
  };

  const handleAddRow = () => {
    const nextRowId =
      _.max(rows.map((row) => Number(row?.id) || 0).concat(0)) + 1;
    const emptyValues = Object.keys(firstRowValues).reduce(
      (prev, curr) => ({
        ...prev,
        [curr]: "",
      }),
      {}
    );
    const newRows = [...rows, { id: nextRowId, ...emptyValues }];
    setRows(newRows);
    syncRowsToForm(newRows);
  };

  const handleRemoveRow = (id) => {
    const filteredRows = rows.filter((row) => row.id !== id);
    const updatedRows = _.map(filteredRows, (elm, index) => ({
      ...elm,
      id: index + 1,
    }));
    setRows(updatedRows);
    syncRowsToForm(updatedRows);
  };

  const onInputChange = (e, id) => {
    const { field_id, value } = e;
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, [field_id]: value } : row
    );
    setRows(updatedRows);
    syncRowsToForm(updatedRows);
  };

  const fieldIdOrder = columnFieldIds?.length
    ? columnFieldIds
    : Object.keys(firstRowValues);

  const getColumnErrorInfo = (id) => {
    const err =
      Object.entries(errors || {}).find(
        ([key, value]) => key.startsWith(`${id}_`) && value
      )?.[1] || null;
    return { hasError: Boolean(err), err: err };
  };

  const result = rows.map((elm) => {
    return fieldIdOrder.reduce(
      (prev, curr) => ({
        ...prev,
        [`${_.indexOf(fieldIdOrder, curr) + 1}_${curr}`]: elm[curr],
      }),
      { "0_id": elm?.id }
    );
  });
  return (
    <form className="table-balancesheet" id={`form-${moduleId}`}>
      <table className="fo-table" id={`table-${moduleId}`}>
        <thead>
          <tr>
            {_.map(tableHeader, (e, index) => {
              const { className = "", height = 21 } =
                BALANCESHEET_TABLE_ICON_SETTINGS[e.icon] || {};
              const valueClassName =
                e.value === "#" ? "first-row-value-position" : "";
              const colFieldId = fieldIdOrder[index];
              const { hasError, err } = getColumnErrorInfo(colFieldId);

              return (
                <th key={index} style={{ width: e.width }}>
                  <div className="period-table-header">
                    {e.icon && (
                      <Icon
                        icon={e.icon}
                        height={height}
                        color="gray-200"
                        marginLeft={5}
                        className={className}
                      />
                    )}
                    <div
                      className={`${valueClassName} ${
                        isValidated && hasError ? "label-error" : ""
                      }`}
                    >
                      {e.value}
                    </div>
                    {isValidated && hasError && (
                      <>
                        <ErrorPopupBtn
                          setShowErrorPopup={() => setShowErrorPopup(index)}
                        />
                        {showErrorPopup === index && (
                          <ErrorPopup
                            errors={err}
                            showErrorPopup
                            setShowErrorPopup={() => setShowErrorPopup(null)}
                            label={e.value}
                            errorMessage={err?.message}
                            additionalErrorMessage={err?.additionalMessage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody id={`body-${moduleId}`}>
          {result.length > 0 ? (
            result.map((row, i) => (
              <TableRow
                key={row["0_id"] ?? i}
                row={row}
                onRemoveRow={handleRemoveRow}
                onInputChange={onInputChange}
                fields={fields}
                isEditable={isEditable}
                lineId={i + 1}
                readOnly={readOnly}
                values={values}
                isValidated={isValidated}
                errors={errors}
                isExport={isExport}
              />
            ))
          ) : (
            <tr>
              <td colSpan={tableHeader.length} className="empty-td">
                <span>
                  <FormattedMessage
                    id="no_info"
                    defaultMessage="Aucune information à montrer"
                  />
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isEditable && !readOnly && !isExport &&
       (
        <div
          style={{ cursor: "pointer", width: "180px", marginTop: "20px" }}
          onClick={handleAddRow}
        >
          <Typography
            className="link-1 violet "
            type="h2"
            label={<>+ {formattedStructure.incrLbl}</>}
          />
        </div>
      )}
    </form>
  );
};

const TableRow = ({
  row,
  fields,
  onRemoveRow,
  isEditable,
  onInputChange,
  lineId,
  readOnly,
  values,
  disableCalendar,
  isValidated,
  errors,
  isExport,
}) => {
  const formattedRow = Object.keys(row).reduce(
    (prev, curr) => ({
      ...prev,
      [curr.substring(2)]: row[curr],
    }),
    {}
  );
  const { id } = formattedRow;

  return (
    <tr>
      {Object.keys(row).map((field, index) => {
        const fieldId = Number(field.substring(2)) || "id";
        const rowFieldId = `${fieldId}_${lineId}`;
        const hasError = Boolean(errors?.[rowFieldId]);
        const selectedField =
          _.find(fields, (f) => f.id === rowFieldId) ||
          _.find(fields, (f) => f.field_id === fieldId); // fallback if rowFieldId is not defined
        if (fieldId !== "id" && selectedField) {
          return (
            <td key={index}>
              <div
                className={`${
                  isValidated && hasError
                    ? "errorCellColor"
                    : "tablePaddingCell"
                }`}
              >
                {displayField({
                  field: selectedField,
                  lineId: lineId,
                  onChange: (e) => {
                    onInputChange(
                      { field_id: e?.field_id, value: e.value },
                      id
                    );
                  },
                  values,
                  labelValue: lineId,
                  readOnly,
                  disableCalendar,
                  hideCalenderIcon: true,
                  inputStyle: { fontWeight: "bold" },
                  isExport,
                  multilineReadOnly: true,
                })}
              </div>
            </td>
          );
        }
        return null;
      })}

      {isEditable && !readOnly && !isExport && (
        <td>
          <div
            style={{ cursor: "pointer", paddingLeft: "10px" }}
            onClick={() => onRemoveRow(id)}
          >
            <Typography
              className="link-1 violet "
              type="h2"
              label={
                <FormattedMessage id="remove_row" defaultMessage="Supprimer" />
              }
            />
          </div>
        </td>
      )}
    </tr>
  );
};

TableBalancesheet.propTypes = {
  tableHeader: PropTypes.array,
  isEditable: PropTypes.bool,
  fields: PropTypes.shape({
    length: PropTypes.number,
  }),
  firstRowValues: PropTypes.object,
  columnFieldIds: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  formattedStructure: PropTypes.shape({
    showEmptyRowOnInit: PropTypes.bool,
    incrLbl: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  values: PropTypes.object,
  rows: PropTypes.shape({
    length: PropTypes.number,
  }),
  setRows: PropTypes.func,
  moduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isValidated: PropTypes.bool,
  errors: PropTypes.object,
};

TableRow.propTypes = {
  row: PropTypes.object,
  fields: PropTypes.array,
  onRemoveRow: PropTypes.func,
  isEditable: PropTypes.bool,
  isExport: PropTypes.bool,
  onInputChange: PropTypes.func,
  lineId: PropTypes.number,
  readOnly: PropTypes.bool,
  values: PropTypes.object,
  disableCalendar: PropTypes.bool,
  isValidated: PropTypes.bool,
  errors: PropTypes.object,
};

export default TableBalancesheet;
